import {
    Cancel,
    CheckCircle,
    FileUploadRounded,
    FilterAltRounded,
    ReplayRounded,
    SearchRounded,
    TextSnippet
} from '@mui/icons-material';
import {Button, Chip, Grid, IconButton, MenuItem, Paper, Stack, TableCell, TextField, useTheme} from '@mui/material';
import {ColDef, GetRowIdParams, GridApi, GridOptions, IServerSideGetRowsParams} from 'ag-grid-enterprise';
import {AgGridReact} from 'ag-grid-react';
import * as React from 'react';
import {useContext, useEffect, useRef, useState} from 'react';
import {Controller, useForm} from "react-hook-form";
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import dayjs from "dayjs";
import {useHasScope} from '../../../../config/globalConfig';
import AgGridCustomTable from '../../../../util/component/AgGridCustomTable';
import SectionHeader from '../../../../util/component/SectionHeader';
import ContentBody from '../../../network/common/content-body/ContentBody';
import {useVoucherActions} from '../../service/useMobilityVoucherAction';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import {useStatusValuesOrder} from "../../component/UserLegalEntitiesOrDealersInputGroup/useStatusValuesOrder";
import {DatePicker} from '@mui/x-date-pickers';
import * as Yup from 'yup';
import {yupResolver} from "@hookform/resolvers/yup";
import {useRecoilState} from 'recoil';
import {STEPPER_STATE} from '../../service/MobilityVoucherRecoil';
import {GlobalContext} from '../../../../util/state/GlobalContextProvider';
import {snackbarTypes} from '../../../../util/state/globalVariables';
import {setSnackbar} from '../../../../util/state/globalAction';
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";

const MobilityVoucherOrders = () => {

    const {t} = useTranslation(['mobilityvoucher']);
    const {t: tg} = useTranslation("translation");
    const theme = useTheme();
    const gridRef = useRef<AgGridReact>(null);
    const navigate = useNavigate();
    const hasScope = useHasScope;
    const {getOrders, exportOrders} = useVoucherActions();
    const [showExtraFilters, setShowExtraFilters] = useState(false);
    const [activeStep, setActiveStep] = useRecoilState(STEPPER_STATE);
    const {globalData: {user}, dispatchGlobalData} = useContext(GlobalContext);

    const legalEntityOptions: any[] = user.legalEntities
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tg(`legalEntities.${x.name}`),
        }));

    const dealerOptions: any[] = user.dealers
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tg(`dealers.${x.name}`),
        }));

    const [dealers, setDealers] = React.useState<any[]>(dealerOptions);
    const toFloatCurrency = (val: string) => {
        return parseFloat(val) / 100 + " €";
    }

    const filterDefaultValues: any = {
        lastName: '',
        taxCode: '',
        companyName: '',
        vatNumber: '',
        email: '',
        status: '',
        voucherCode: '',
        voucherState: '',
        dateTo: null,
        dateFrom: null,
        amountFrom: '',
        amountTo: '',
        legalEntities: legalEntityOptions.map((x: any) => x.value),
        dealers: [],
    };

    const [filters, setFilters] = useState<any>(filterDefaultValues);

    const validationObj = {
        dateFrom: Yup.date().nullable()
            .test("testdateFrom", t('errors.datesCheck'), (value, context) => {
                return (!value || !context.parent.dateTo || value <= context.parent.dateTo)
            })
            .typeError(t('errors.invalidDate')),
        dateTo: Yup.date().nullable()
            .test("testdateTo", t('errors.datesCheck'), (value, context) => {
                return (!value?.setDate(value.getDate() + 1) || !context.parent.dateFrom || value >= context.parent.dateFrom)
            })
            .typeError(t('errors.invalidDate')),
        amountFrom: Yup.number().transform((value, originalValue) => {
            return typeof originalValue === 'string' && originalValue.trim() === '' ? undefined : parseFloat(originalValue) * 100;
        }).test('amount', t('errors.amountCheck'), function (value) {
            const toValue = this.parent.amountTo;
            return toValue === undefined || value === undefined || value <= toValue || toValue <= value;
        }).nullable(),
        amountTo: Yup.number().transform((value, originalValue) => {
            return typeof originalValue === 'string' && originalValue.trim() === '' ? undefined : parseFloat(originalValue) * 100;
        }).test('amount', t('errors.amountCheck'), function (value) {
            const fromValue = this.parent.amountFrom;
            return fromValue === undefined || value === undefined || value >= fromValue;
        }).nullable(),
        dealers: Yup.array(),
        legalEntities: Yup.array().min(1, tg("validation.selectAtLeastOne")),
    }


    const {getStatusColorOrder, getStatusLabelOrder} = useStatusValuesOrder();
    const [searchInput, setSearchInput] = useState("");
    const {
        watch,
        control,
        handleSubmit,
        formState,
        reset,
        resetField,
    } = useForm({defaultValues: filterDefaultValues, resolver: yupResolver(Yup.object(validationObj))});

    const watchAll = watch();

    const exportOrdersVoucher = async (filters: any) => {
        console.log(filters);
        try {
            const response = await exportOrders(filters);
            var binaryData = [];
            binaryData.push(response);
            var url = window.URL.createObjectURL(new Blob(binaryData, {type: response.type}));
            var a = document.createElement('a');
            document.body.appendChild(a);
            a.setAttribute('style', 'display: none');
            a.setAttribute('target', 'blank');
            a.href = url;
            a.download = 'orders';
            a.click();
            window.URL.revokeObjectURL(url);
            a.remove();

        } catch (err) {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t("downloadError"),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    }
                }
            });
        }
    }

    function createDatasource(filters: any, api: GridApi) {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let filterTemp = Object.assign({}, filters);
                filterTemp.offset = params.request.startRow ? params.request.startRow : 0;
                filterTemp.limit = (params.request.endRow ? params.request.endRow : 10) - filterTemp.offset;
                filterTemp.sort = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].colId;
                filterTemp.order = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].sort;
                filterTemp.search = searchInput;
                let data = await getOrders(filterTemp);

                params.success({
                    rowData: data?.content,
                    rowCount: data?.totalElements
                });
                if (data.totalElements === 0) {
                    api.showNoRowsOverlay();
                } else {
                    api.hideOverlay();
                }
            }
        };
    }

    useEffect(() => {
        gridRef?.current?.api?.setServerSideDatasource(createDatasource(filters, gridRef.current!.api));
    }, [filters]);


    const detailsOrder = async (id: any) => {
        window.open('/mobilityvoucher/details-order/' + id);
    }


    const columns: ColDef[] = [
        {
            field: "orderNumber",
            headerName: t('ordertable.order'),
            unSortIcon: false,
        },
        {
            field: "orderDate",
            headerName: t('ordertable.data'),
            unSortIcon: false,
            valueFormatter: params => params.value ? dayjs(params.value).format("lll") : ''
        },
        {
            field: "customer",
            headerName: t('ordertable.customer'),
            unSortIcon: false,
            cellRenderer: (params: any) => {
                if (params.data.customer.companyName) {
                    return (
                        <TableCell align="left">
                            {params.data.customer.companyName}
                        </TableCell>
                    );
                } else {
                    return (
                        <TableCell align="left">
                            {params.data.customer.firstName} {params.data.customer.lastName}
                        </TableCell>
                    );
                }
            }
        },
        {
            field: "subscription",
            headerName: t('ordertable.subscription'),
            unSortIcon: false,
            cellRenderer: (params: any) => {
                return (
                    <TableCell align="left">
                        {params.data.subscription.productCode} - {params.data.subscription.name}
                    </TableCell>
                );
            }
        },
        {
            field: "voucherCode",
            headerName: t('ordertable.voucher'),
            unSortIcon: false,
        },
        {
            field: "voucherState",
            headerName: t('ordertable.voucherState'),
            width: 100,
            sortable: false,
            cellRenderer: (params: any) => {
                return (
                    <>
                        {params.data.voucherCode ?
                            <>
                                {params.data.voucherState === "AVAILABLE" ?
                                    <CheckCircle sx={{mt: theme.spacing(1)}}/>
                                    :
                                    <Cancel sx={{mt: theme.spacing(1)}}/>
                                }
                            </>
                            :
                            <></>
                        }
                    </>
                );
            }
        },
        {
            field: "amount",
            headerName: t('ordertable.amount'),
            unSortIcon: false,
            valueGetter: params => {
                return toFloatCurrency(params.data.amount);
            }
        },
        {
            field: "orderState",
            headerName: t('ordertable.state'),
            unSortIcon: false,
            cellRenderer: (params: any) => {
                return <Chip label={getStatusLabelOrder(params.data.orderState)}
                             sx={{backgroundColor: getStatusColorOrder(params.data.orderState)}}/>
            }
        },
        {
            field: "tenantId",
            headerName: t('ordertable.tenantId'),
            unSortIcon: false,
        },
        {
            field: "dealerId",
            headerName: t('ordertable.dealerId'),
            unSortIcon: false,
        },
        {
            field: "_actions",
            headerName: "",
            pinned: 'right',
            lockPosition: 'right',
            lockPinned: false,
            maxWidth: 160,
            sortable: false,
            cellRenderer: (params: any) => {
                return (
                    <>{hasScope('SCOPE_MV_ORDERS_DETAILS_VIEW') ?
                        <Stack direction={"row"} justifyContent={"center"}>
                            <IconButton onClick={() => detailsOrder(params.data.id)} color="accent">
                                <TextSnippet/>
                            </IconButton>
                        </Stack> : <></>
                    }
                    </>
                );
            }
        },
    ];

    const defaultColDef = {
        resizable: true,
    };


    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data['orderNumber'],
        suppressRowClickSelection: true,
        domLayout: 'autoHeight',
        rowModelType: 'serverSide',
        defaultColDef: {
            sortable: true,
            resizable: true,
            minWidth: 130
        },
        columnDefs: columns
    };


    const onFormSubmit = (data: any) => {
        setFilters(data);
        setShowExtraFilters(false)
        // gridRef?.current?.api?.setServerSideDatasource(createDatasource(filters, gridRef.current!.api));
    };

    const resetForm = () => {
        reset(filterDefaultValues);
        setShowExtraFilters(false)
        onFormSubmit({})

    }

    const addNewOrder = () => {
        navigate('/mobilityvoucher/new-order');
        setActiveStep((prevActiveStep: number) => 0);
    };

    const onErrors = (errors: any) => console.error(errors);

    const toolbar = {
        renderActions: () =>
            <>
                <>{hasScope('SCOPE_MV_ORDERS_EXPORT') ?
                    <Button onClick={() => exportOrdersVoucher(filters)} size="smallPlus"
                            startIcon={<FileUploadRounded/>}>
                        {t('buttons.exportOrders')}
                    </Button> : <></>
                }
                </>
                <>{hasScope('SCOPE_MV_ORDERS_CREATE') ?
                    <Button onClick={addNewOrder} variant="contained" size='smallPlus' startIcon={<LibraryAddIcon/>}>
                        {t('buttons.newOrder')}
                    </Button> : <></>
                }
                </>

            </>
    }

    const renderdateFrom = (field: any, error: any, formState: any, values: any) => (
        <DatePicker
            views={['day']}
            label={t('filterBox.orderDateFrom')}
            value={field.value}
            onChange={field.onChange}
            renderInput={(params) =>
                <TextField
                    {...params}
                    fullWidth
                    id={"dateFrom"}
                    error={error !== undefined}
                    helperText={error ? error.message : ""}/>
            }
            inputFormat={"L"}
        />)

    const renderdateTo = (field: any, error: any, formState: any, values: any) => (
        <DatePicker
            views={['day']}
            label={t('filterBox.orderDateTo')}
            value={field.value}
            minDate={values["dateFrom"] ? values["dateFrom"] : ""}
            onChange={field.onChange}
            renderInput={(params) =>
                <TextField
                    {...params}
                    fullWidth
                    id={"dateTo"}
                    error={error !== undefined}
                    helperText={error ? error.message : ""}/>
            }
            inputFormat={"L"}
        />)

    const fieldsDate: any = {
        dateFrom: renderdateFrom,
        dateTo: renderdateTo,
    }


    return (
        <>
            <SectionHeader title={t('orders.header.title')} subtitle={t('orders.header.subTitle')}/>
            <ContentBody>
                <Paper elevation={1} sx={{p: theme.spacing(2), mb: theme.spacing(2)}}>
                    <form onSubmit={handleSubmit(onFormSubmit, onErrors)} noValidate>
                        <Grid container alignItems="center" item xs={12} columnSpacing={2}>
                            <Grid item lg={3} md={6} xs={12} sx={{display: "flex", alignItems: "center"}}>
                                <Controller
                                    name='lastName'
                                    control={control}
                                    render={({field: {ref, ...field}, fieldState}) => (
                                        <TextField
                                            label={t('filterBox.lastName')}
                                            placeholder={t('filterBox.lastName')}
                                            fullWidth
                                            value={field.value}
                                            defaultValue={''}
                                            onChange={field.onChange}
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message}
                                        >
                                        </TextField>
                                    )}
                                />
                            </Grid>
                            <Grid item lg={3} md={6} xs={12}>
                                <Controller
                                    name='taxCode'
                                    control={control}
                                    render={({field: {ref, ...field}, fieldState}) => (
                                        <TextField
                                            label={t('filterBox.taxCode')}
                                            placeholder={t('filterBox.taxCode')}
                                            fullWidth
                                            value={field.value}
                                            defaultValue={''}
                                            onChange={field.onChange}
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message}
                                        >
                                        </TextField>
                                    )}
                                />
                            </Grid>

                            <Grid item lg={3} md={6} xs={12}>
                                <Controller
                                    name='status'
                                    control={control}
                                    render={({field: {ref, ...field}, fieldState}) => (
                                        <TextField
                                            select
                                            label={t('filterBox.status')}
                                            placeholder={t('filterBox.status')}
                                            fullWidth
                                            value={field.value}
                                            defaultValue={''}
                                            onChange={field.onChange}
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message}
                                        >
                                            <MenuItem value=''></MenuItem>
                                            <MenuItem value='COMPLETED'>{t('status.completed')}</MenuItem>
                                            <MenuItem value='NOT_COMPLETED'>{t('status.notCompleted')}</MenuItem>
                                            <MenuItem value='REFUNDED'>{t('status.refunded')}</MenuItem>
                                        </TextField>
                                    )}
                                />
                            </Grid>

                            <Grid item lg={3} md={6} xs={12}>
                                <Stack direction="row" alignItems="center" justifyContent="end" columnGap={2}>
                                    <Button variant="text" startIcon={<ReplayRounded/>} onClick={resetForm}>
                                        {t("searchTooltip.clearFilters")}
                                    </Button>


                                    <Button variant="text" startIcon={<FilterAltRounded/>}
                                            onClick={() => {
                                                setShowExtraFilters(!showExtraFilters);
                                            }}
                                    >
                                        {showExtraFilters ? t("searchTooltip.closeFilters") : t("searchTooltip.filters")}
                                    </Button>


                                    <Button type="submit" size="icon" color="accent">
                                        <SearchRounded/>
                                    </Button>
                                </Stack>
                            </Grid>

                        </Grid>
                        {showExtraFilters && (
                            <>
                                <Grid container item xs={12} sx={{mt: theme.spacing(2)}} columnSpacing={2}>

                                    {Object.keys(fieldsDate).map(f =>
                                        <Grid item lg={3} md={6} xs={12} key={f}>
                                            <Controller
                                                control={control}
                                                name={f}
                                                render={({field, fieldState: {error}, formState}) => (
                                                    fieldsDate[f](field, error, formState, watchAll)
                                                )}
                                            />
                                        </Grid>
                                    )}


                                    <Grid item lg={3} md={6} xs={12}>
                                        <Controller
                                            name='amountFrom'
                                            control={control}
                                            render={({field: {ref, ...field}, fieldState}) => (
                                                <TextField
                                                    label={t('filterBox.orderAmountFrom')}
                                                    placeholder={t('filterBox.orderAmountFrom')}
                                                    fullWidth
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    error={!!fieldState.error}
                                                    helperText={fieldState.error?.message}
                                                >
                                                </TextField>
                                            )}
                                        />
                                    </Grid>

                                    <Grid item lg={3} md={6} xs={12}>
                                        <Controller
                                            name='amountTo'
                                            control={control}
                                            render={({field: {ref, ...field}, fieldState}) => (
                                                <TextField
                                                    label={t('filterBox.orderAmountTo')}
                                                    placeholder={t('filterBox.orderAmountTo')}
                                                    fullWidth
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    error={!!fieldState.error}
                                                    helperText={fieldState.error?.message}
                                                >
                                                </TextField>
                                            )}
                                        />
                                    </Grid>
                                </Grid>
                                <Grid container item xs={12} sx={{mt: theme.spacing(2)}} columnSpacing={2}>
                                    <Grid item lg={3} md={6} xs={12}>
                                        <Controller
                                            name='voucherCode'
                                            control={control}
                                            render={({field: {ref, ...field}, fieldState}) => (
                                                <TextField
                                                    label={t('filterBox.voucherCode')}
                                                    placeholder={t('filterBox.voucherCode')}
                                                    fullWidth
                                                    value={field.value}
                                                    defaultValue={''}
                                                    onChange={field.onChange}
                                                    error={!!fieldState.error}
                                                    helperText={fieldState.error?.message}
                                                >
                                                </TextField>
                                            )}
                                        />
                                    </Grid>

                                    <Grid item lg={3} md={6} xs={12}>
                                        <Controller
                                            name='email'
                                            control={control}
                                            render={({field: {ref, ...field}, fieldState}) => (
                                                <TextField
                                                    label={t('filterBox.email')}
                                                    placeholder={t('filterBox.email')}
                                                    fullWidth
                                                    value={field.value}
                                                    defaultValue={''}
                                                    onChange={field.onChange}
                                                    error={!!fieldState.error}
                                                    helperText={fieldState.error?.message}
                                                >
                                                </TextField>
                                            )}
                                        />
                                    </Grid>


                                    <Grid item lg={3} md={6} xs={12} sx={{display: "flex", alignItems: "center"}}>
                                        <Controller
                                            name='companyName'
                                            control={control}
                                            render={({field: {ref, ...field}, fieldState}) => (
                                                <TextField
                                                    label={t('filterBox.companyName')}
                                                    placeholder={t('filterBox.companyName')}
                                                    fullWidth
                                                    value={field.value}
                                                    defaultValue={''}
                                                    onChange={field.onChange}
                                                    error={!!fieldState.error}
                                                    helperText={fieldState.error?.message}
                                                >
                                                </TextField>
                                            )}
                                        />
                                    </Grid>


                                    <Grid item lg={3} md={6} xs={12}>
                                        <Controller
                                            name='vatNumber'
                                            control={control}
                                            render={({field: {ref, ...field}, fieldState}) => (
                                                <TextField
                                                    label={t('filterBox.vatNumber')}
                                                    placeholder={t('filterBox.vatNumber')}
                                                    fullWidth
                                                    value={field.value}
                                                    defaultValue={''}
                                                    onChange={field.onChange}
                                                    error={!!fieldState.error}
                                                    helperText={fieldState.error?.message}
                                                >
                                                </TextField>
                                            )}
                                        />
                                    </Grid>

                                    <Grid item lg={3} md={6} xs={12}>
                                        <Controller
                                            name='voucherState'
                                            control={control}
                                            render={({field: {ref, ...field}, fieldState}) => (
                                                <TextField
                                                    select
                                                    label={t('filterBox.voucherState')}
                                                    placeholder={t('filterBox.voucherState')}
                                                    fullWidth
                                                    value={field.value}
                                                    defaultValue={''}
                                                    onChange={field.onChange}
                                                    error={!!fieldState.error}
                                                    helperText={fieldState.error?.message}
                                                >
                                                    <MenuItem value=''></MenuItem>
                                                    <MenuItem value='AVAILABLE'>{t('filterBox.available')}</MenuItem>
                                                    <MenuItem value='ON_HOLD'>{t('filterBox.onHold')}</MenuItem>
                                                </TextField>
                                            )}
                                        />
                                    </Grid>
                                    <Grid item lg={3} md={6} xs={12}>
                                        <Controller
                                            control={control}
                                            name={"legalEntities"}
                                            render={({field}) => (
                                                <TextField
                                                    fullWidth
                                                    label={tg("shared.legalEntities")}
                                                    select
                                                    SelectProps={{
                                                        multiple: true,
                                                        renderValue: (selected: any) =>
                                                            selected
                                                                .map((s: any) => legalEntityOptions.find((ss) => ss.value === s)!.label)
                                                                .join(", "),
                                                    }}
                                                    value={field.value}
                                                    onChange={(event) => {
                                                        setDealers([]);
                                                        let dealers = user?.dealers
                                                            ?.filter(
                                                                (x: any) =>
                                                                    event.target.value?.length > 0 &&
                                                                    x.legalEntities.some((e: any) => event.target.value.includes(e.name)) &&
                                                                    x.name.toLowerCase() !== "all"
                                                            )
                                                            .map((x: any) => {
                                                                return {
                                                                    label: tg(`dealers.${x.name}`),
                                                                    value: x.name,
                                                                };
                                                            });
                                                        setDealers(dealers);
                                                        field.onChange(event)
                                                    }
                                                    }
                                                >
                                                    {legalEntityOptions.map((item) => (
                                                        <MenuItem key={item.value} value={item.value}>
                                                            <Checkbox checked={field.value?.indexOf(item.value) > -1}/>
                                                            <ListItemText primary={item.label}/>
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            )}/>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Controller
                                            control={control}
                                            name={"dealers"}
                                            render={({field}) => (
                                                <TextField
                                                    fullWidth
                                                    label={tg("shared.dealers")}
                                                    select
                                                    SelectProps={{
                                                        multiple: true,
                                                        renderValue: (selected: any) =>
                                                            selected
                                                                .map((s: any) => dealers.find((ss) => ss.value === s)!.label)
                                                                .join(", "),
                                                    }}
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                >
                                                    {dealers.map((item) => (
                                                        <MenuItem key={item.value} value={item.value}>
                                                            <Checkbox checked={field.value?.indexOf(item.value) > -1}/>
                                                            <ListItemText primary={item.label}/>
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            )}/>
                                    </Grid>
                                </Grid>
                            </>
                        )}
                    </form>
                </Paper>
                <Grid>
                    <Grid item xs={12}>
                        <AgGridCustomTable gridOptions={gridOptions} gridRef={gridRef} toolbar={toolbar}
                                           onGridReady={(params) => {
                                               params.api.setServerSideDatasource(createDatasource(filters, gridRef.current!.api));
                                           }}
                                           defaultColDef={defaultColDef}/>
                    </Grid>
                </Grid>
            </ContentBody>

        </>
    );
}

export default MobilityVoucherOrders;